import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { post, session } from '@/lib/api';
import type { IssuedTokens, SessionUser } from '@his/shared';
import { describeError, useAuth } from '@/lib/auth';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

/**
 * TOTP enrolment (SRS 4.3.4 — mandatory for admin and finance roles).
 *
 * Reached with an enrolment-scoped token: the API refuses every other route
 * until this is finished, so a newly created Super Admin has exactly one thing
 * they can do, and it is this.
 */
export function TwoFactorSetupPage() {
  const { user, signOut, refreshUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const setup = useQuery({
    queryKey: ['2fa-setup'],
    queryFn: () => post<{ secret: string; otpauthUrl: string }>('/auth/2fa/setup'),
    enabled: Boolean(user),
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (user && !user.requires2fa && !user.has2fa) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const confirm = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Enrolment now hands back a real session: the person has just proved a
      // password and a code, which is the whole of a two-factor sign-in. They
      // used to be dropped at the login form to type the same password again —
      // and on a temporary password, a third time after changing it.
      const enrolled = await post<{ tokens: IssuedTokens; user: SessionUser } | null>('/auth/2fa/enable', {
        totp: code,
      });
      toast.success('Two-factor authentication is on', 'Keep your authenticator to hand — you will need it each time.');

      if (enrolled?.tokens) {
        session.setTokens(enrolled.tokens);
        await refreshUser();
        const landing = enrolled.user.isPlatformUser
          ? enrolled.user.platformRole === 'contact_person'
            ? '/client'
            : '/owner'
          : enrolled.user.mustChangePassword
            ? '/change-password'
            : '/dashboard';
        navigate(landing, { replace: true });
        return;
      }

      // Platform staff keep the sign-in they already hold.
      await signOut();
      navigate('/login', { replace: true });
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Set up two-factor authentication</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Your role has access to administrative and financial functions, so a second factor is required.
            Add this secret to Google Authenticator, Authy or 1Password, then enter the code it shows.
          </p>

          {setup.isLoading && <p className="text-sm text-muted-foreground">Preparing your secret…</p>}
          {setup.error && <Alert variant="destructive">{describeError(setup.error)}</Alert>}

          {setup.data && (
            <div className="rounded-md border bg-muted/40 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Secret key</p>
              <p className="mt-1 select-all break-all font-mono text-sm">{setup.data.secret}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                Account: {user?.email ?? user?.displayName}
              </p>
            </div>
          )}

          <form onSubmit={confirm} className="space-y-4">
            <Field label="Six-digit code" htmlFor="totp" required>
              <Input
                id="totp"
                inputMode="numeric"
                maxLength={6}
                className="tracking-[0.4em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
              />
            </Field>

            {error && <Alert variant="destructive">{error}</Alert>}

            <Button type="submit" className="w-full" loading={busy} disabled={code.length !== 6}>
              Turn on two-factor authentication
            </Button>
          </form>

          <button
            type="button"
            className="block w-full text-center text-xs text-muted-foreground underline-offset-4 hover:underline"
            onClick={async () => {
              await signOut();
              navigate('/login', { replace: true });
            }}
          >
            Sign out instead
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
